import { noop } from 'lodash';
import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { AcknowledgableSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { AiSuggestionAcceptanceStatus } from '@/types/schema';

export interface AcknowledgedSuggestion {
  suggestionID: string;
  status: AiSuggestionAcceptanceStatus;
  clientProfileId?: string;
  clientOrganizationId?: string;
  entityId?: string;
  testamentaryEntityId?: string;
}

export interface SuggestionAcknowledgementContext {
  /**
   * Acknowledge a suggestion, either accepting or rejecting it. This will
   * update the acknowledgedSuggestions state, for the current edit session.
   */
  acknowledgeSuggestion: (suggestion: AcknowledgedSuggestion) => void;
  /**
   * The suggestions that have been acknowledged by the user during an edit session.
   * This is used to hide suggestion while the user is editing, before they have
   * saved the form. This gets reset when the user navigates to a different section,
   * or closes the form.
   */
  acknowledgedSuggestions: Record<string, AcknowledgedSuggestion>;

  /**
   * Get the acknowledged suggestions for a given matchedId. Will be one of:
   * [clientProfileId, clientOrganizationId, entityId, testamentaryEntityId].
   * @param matchedId
   */
  getAcknowledgedSuggestion: (matchedId: string) => AcknowledgedSuggestion[];

  /**
   * The previously acknowledged suggestions. This is used to determine if a
   * suggestion was acknowledged during a previous edit session.
   * init() is called after fetching the suggestions from the backend by  the
   * EntitySuggestionsProvider and DPSuggestionsProvider.
   */
  previouslyAcknowledgedSuggestions: Record<string, AcknowledgedSuggestion>;
  initPreviouslyAcknowledgedSuggestions: (
    suggestions: AcknowledgableSuggestion[]
  ) => void;

  /**
   * Resets the acknowledged suggestions for the current edit session. This
   * should be called when we close the form, or navigate to a different section.
   */
  resetAcknowledgedSuggestions: () => void;
}

export const getDefaultValues = (): SuggestionAcknowledgementContext => ({
  acknowledgeSuggestion: noop,
  acknowledgedSuggestions: {},
  getAcknowledgedSuggestion: () => [],
  previouslyAcknowledgedSuggestions: {},
  initPreviouslyAcknowledgedSuggestions: noop,
  resetAcknowledgedSuggestions: noop,
});

export const SuggestionAcknowledgementContext =
  createContext<SuggestionAcknowledgementContext>(getDefaultValues());

export const useSuggestionAcknowledgementContext = () => {
  return useGuardedContext(
    SuggestionAcknowledgementContext,
    'SuggestionAcknowledgementProvider'
  );
};
